import {
  UnitDataListReportsDateUnit,
  changeUnitDataListNameDateUnit,
} from '../../utils/unitDataListDateRange';
import { generateStartEndFromYmdLists } from '../generateStartEndFromYmdLists';
import { DataDai } from './types';

export type DataDaiDateFieldsForApi = {
  ymdList?: string[];
  ymdComparisonList?: string[];
  startDate?: string;
  endDate?: string;
  startComparisonDate?: string;
  endComparisonDate?: string;
};

export type DataDaiApiDateParams =
  | { ymdList: string[] | undefined }
  | {
      ymdList: string[] | undefined;
      ymdComparisonList: string[] | undefined;
    }
  | {
      startDate: string | undefined;
      endDate: string | undefined;
    }
  | {
      startDate: string | undefined;
      endDate: string | undefined;
      startComparisonDate: string;
      endComparisonDate: string;
    };

/**
 * 台別データ一覧の /data/dai API に載せる日付フィールドを組み立てる。
 *
 * 長期間選択時に ymdList をそのまま送ると URL が肥大化し ALB 414 になるため、
 * 自由選択以外では ymdList を送らず start/end のみを返す（相互排他）。
 *
 * - 自由選択: ymdList（比較時は ymdComparisonList も）
 * - それ以外: startDate / endDate（比較時は startComparisonDate / endComparisonDate も）
 */
export const buildDataDaiApiDateParams = (
  dateFields: DataDaiDateFieldsForApi,
  dateUnit: UnitDataListReportsDateUnit,
  isComparison: boolean
): DataDaiApiDateParams => {
  const normalizedDateUnit = changeUnitDataListNameDateUnit(dateUnit);

  if (normalizedDateUnit === '自由選択') {
    return isComparison
      ? {
          ymdList: dateFields.ymdList,
          ymdComparisonList: dateFields.ymdComparisonList,
        }
      : { ymdList: dateFields.ymdList };
  }

  const apiDateRangeFromYmdLists = generateStartEndFromYmdLists(dateFields);

  if (isComparison) {
    return {
      startDate: apiDateRangeFromYmdLists.startDate,
      endDate: apiDateRangeFromYmdLists.endDate,
      // 比較 ON かつ比較日付が未算出のとき、API は空文字を受け付ける（チェーン店レポートと同仕様）
      startComparisonDate: apiDateRangeFromYmdLists.startComparisonDate ?? '',
      endComparisonDate: apiDateRangeFromYmdLists.endComparisonDate ?? '',
    };
  }

  return {
    startDate: apiDateRangeFromYmdLists.startDate,
    endDate: apiDateRangeFromYmdLists.endDate,
  };
};

/**
 * 検索条件から日付フィールドを除外し、API 送信用の日付パラメータに差し替える。
 * 旧 ymdList / startDate 等は destructuring で落とし、buildDataDaiApiDateParams の結果だけを載せる。
 */
export const transformDataDaiSettingForApi = (
  setting: DataDai['setting'],
  dateUnit: UnitDataListReportsDateUnit,
  isComparison: boolean
): DataDai['setting'] => {
  const {
    ymdList,
    ymdComparisonList,
    startDate,
    endDate,
    startComparisonDate,
    endComparisonDate,
    ...rest
  } = setting ?? {};

  return {
    ...rest,
    ...buildDataDaiApiDateParams(
      {
        ymdList,
        ymdComparisonList,
        startDate,
        endDate,
        startComparisonDate,
        endComparisonDate,
      },
      dateUnit,
      isComparison
    ),
  };
};
